/**
 * Міст між ngx-translate і «статичним» кодом (константи, мапи, енами).
 *
 * Історично проєкт робив `require('assets/i18n/ua.json')` прямо в `enum/constants.ts`.
 * Це мало два наслідки: обидві локалі цілком потрапляли в JS-бандл (ліниве
 * завантаження ставало безглуздим), а всі похідні константи «застигали»
 * мовою, яка була на момент завантаження бандла — тому мову й доводилось
 * міняти через перезавантаження сторінки.
 *
 * Тут той самий API (`languages['ua'].errors.foo`) віддається поверх живого
 * словника `TranslateService`. Плюс `liveObject()` — обгортка для констант,
 * які будуються з перекладів: вона перераховує об'єкт, щойно словник змінився.
 */

type Dict = Record<string, any>;

/**
 * Вузол словника перекладів: неймспейс, вкладена група або готовий рядок.
 *
 * Навмисно `any`, а не `Record<string, unknown>`: у проєкті ввімкнено
 * `noPropertyAccessFromIndexSignature`, тож будь-який тип з індексною сигнатурою
 * заборонив би крапкову нотацію (`activeTranslations.errors.default_error`),
 * якою користуються сотні місць. Структура JSON-локалей однаково не типізована.
 */
export type TranslationNode = any;

/** `<lang>` -> плаский словник цієї мови (base + усі підвантажені модулі). */
let store: Record<string, Dict> = {};

/** Лічильник змін словника. Зростає на кожен апдейт — на нього дивиться liveObject. */
let version = 0;

const listeners = new Set<() => void>();

/** Мова, з якої читають, коли її не вказали явно. */
let activeLanguage = 'ua';

/** Викликається з TranslationManagerService на кожну зміну перекладів. */
export function syncLiveTranslations(language: string, dictionary: Dict, makeActive = true): void {
  store = { ...store, [language]: dictionary ?? {} };
  if (makeActive) {
    activeLanguage = language;
  }
  version += 1;
  listeners.forEach((listener) => listener());
}

/** Підписка на зміну словника; колбек одразу викликається один раз. */
export function onLiveTranslationsChange(listener: () => void): () => void {
  listeners.add(listener);
  listener();
  return () => listeners.delete(listener);
}

export function liveTranslationsVersion(): number {
  return version;
}

/** Заглушка для неймспейсу, який ще не приїхав: будь-яке поле — порожній рядок. */
const EMPTY_GROUP: Dict = new Proxy(
  {},
  {
    get: (_target: Dict, prop: string | symbol): unknown => {
      if (prop === Symbol.toPrimitive) {
        return () => '';
      }
      if (prop === 'toString' || prop === 'valueOf') {
        return () => '';
      }
      return typeof prop === 'string' ? '' : undefined;
    },
  },
);

function languageView(language: string): Dict {
  return new Proxy(
    {},
    {
      get: (_target: Dict, namespace: string | symbol): unknown => {
        if (typeof namespace !== 'string') {
          return undefined;
        }
        const value: unknown = store[language]?.[namespace];
        return value === undefined || value === null ? EMPTY_GROUP : value;
      },
      has: (_target, namespace) => typeof namespace === 'string' && namespace in (store[language] ?? {}),
      ownKeys: () => Reflect.ownKeys(store[language] ?? {}),
      getOwnPropertyDescriptor: (_target, namespace) => ({
        configurable: true,
        enumerable: true,
        value: (store[language] ?? {})[namespace as string],
      }),
    },
  );
}

/**
 * Drop-in заміна старого `languages` з `enum/constants.ts`.
 * `languages['ua'].errors.default_error` працює як раніше, але читає живий словник.
 */
export const languages: Record<string, any> = new Proxy(
  {},
  {
    get: (_target, language) => (typeof language === 'string' ? languageView(language) : undefined),
    has: (_target, language) => typeof language === 'string' && language in store,
    ownKeys: () => Reflect.ownKeys(store),
    getOwnPropertyDescriptor: () => ({ configurable: true, enumerable: true }),
  },
);

/** Поточна мова, яку бачить статичний код. */
export function activeLiveLanguage(): string {
  return activeLanguage;
}

/**
 * Словник **активної** мови — головний спосіб читати переклади зі «статичного» коду.
 *
 *   activeTranslations.notifications_texts.error.error
 *
 * Прийшов на заміну `languages[localStorage.getItem('language') || 'ua']`:
 * той патерн брав мову зі сховища на кожному виклику, тому міг розійтись із
 * тим, що реально завантажено в `TranslateService`, і не давав жодної гарантії,
 * що словник цієї мови взагалі є. Тут мова завжди та, яку зараз показує застосунок.
 */
export const activeTranslations: TranslationNode = new Proxy(
  {},
  {
    get: (_target: Dict, namespace: string | symbol): unknown =>
      typeof namespace === 'string' ? (languages[activeLanguage] as Dict)[namespace] : undefined,
    has: (_target: Dict, namespace: string | symbol) =>
      typeof namespace === 'string' && namespace in (store[activeLanguage] ?? {}),
    ownKeys: () => Reflect.ownKeys(store[activeLanguage] ?? {}),
    getOwnPropertyDescriptor: (_target: Dict, namespace: string | symbol) => ({
      configurable: true,
      enumerable: true,
      value: (store[activeLanguage] ?? {})[namespace as string],
    }),
  },
);

export interface LiveObjectOptions<T> {
  /** Значення для ключа, якого немає в об'єкті (наприклад, невідомий код помилки). */
  fallback?: (prop: string, value: T) => unknown;
}

/**
 * Обгортає константу, побудовану з перекладів, так щоб вона перераховувалась
 * після зміни мови / дозавантаження модуля.
 *
 * Фабрика викликається ліниво й лише тоді, коли словник справді змінився,
 * тож накладні витрати — одне порівняння числа на звернення.
 */
export function liveObject<T extends object>(factory: () => T, options: LiveObjectOptions<T> = {}): T {
  const skeleton = factory();
  let cache: T = skeleton;
  let cachedVersion = version;

  const resolve = (): T => {
    if (cachedVersion !== version) {
      cache = factory();
      cachedVersion = version;
    }
    return cache;
  };

  return new Proxy(skeleton, {
    get: (_target: T, prop: string | symbol): unknown => {
      const current = resolve();
      const value: unknown = (current as unknown as Dict)[prop as string];
      if (value === undefined && typeof prop === 'string' && options.fallback) {
        return options.fallback(prop, current);
      }
      return value;
    },
    set: (_target: T, prop: string | symbol, value: unknown): boolean => {
      // Пишемо в актуальний об'єкт, а не в скелет, інакше запис і наступне
      // читання розійшлися б. Запис усе одно зникне при перебудові (зміна мови),
      // тому liveObject — лише для структур, які читають.
      (resolve() as unknown as Dict)[prop as string] = value;
      return true;
    },
    has: (_target, prop) => Reflect.has(resolve(), prop),
    ownKeys: () => Reflect.ownKeys(resolve()),
    getOwnPropertyDescriptor: (target, prop) => {
      const current = Reflect.getOwnPropertyDescriptor(resolve(), prop);
      const onTarget = Reflect.getOwnPropertyDescriptor(target, prop);

      // Інваріант Proxy: для non-configurable властивості таргета (наприклад
      // `length` масиву) не можна віддати дескриптор з іншими флагами —
      // рушій кине TypeError. Міняємо тільки значення, флаги лишаємо як є.
      if (onTarget && !onTarget.configurable) {
        return current && 'value' in current ? { ...onTarget, value: current.value } : onTarget;
      }

      return current ? { ...current, configurable: true } : undefined;
    },
  });
}
